import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import MastersHome from './pages/MastersHome';
import EmployeeMaster from './pages/EmployeeMaster';
import DivisionMaster from './pages/DivisionMaster';
import FactoryMaster from './pages/FactoryMaster';
import ShiftLineMaster from './pages/ShiftLineMaster';
import OperationMaster from './pages/OperationMaster';
import FundGroupMaster from './pages/FundGroupMaster';
import MachineMaster from './pages/MachineMaster';
import DowntimeReasonMaster from './pages/DowntimeReasonMaster';
import OperationBulletin from './pages/OperationBulletin';
import LineDesign from './pages/LineDesign';
import Deployment from './pages/Deployment';
import DailyBalancing from './pages/DailyBalancing';
import HourlyReporting from './pages/HourlyReporting';
import AndroidShell from './pages/AndroidShell';
import TvShell from './components/layout/TvShell';
import SkillMatrix from './pages/SkillMatrix';
import SkillJourney from './pages/SkillJourney';
import SupervisorDashboard from './pages/SupervisorDashboard';
import PlantHeadDashboard from './pages/PlantHeadDashboard';
import DowntimeDashboard from './pages/DowntimeDashboard';
import PlantOptimiser from './pages/PlantOptimiser';
import RoleMaster from './pages/RoleMaster';
import UserAccess from './pages/UserAccess';
import MonthlySummary from './pages/MonthlySummary';

const Placeholder = ({ title }) => {
  React.useEffect(() => {
    const el = document.getElementById('lc-page-title');
    if (el) el.textContent = title;
  }, [title]);
  return (
    <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: 'var(--text-primary)' }}>{title}</div>
      <div style={{ fontSize: 12 }}>Coming in next build.</div>
    </div>
  );
};

function AccessDenied() {
  return (
    <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-secondary)' }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: 'var(--text-primary)' }}>Access denied</div>
      <div style={{ fontSize: 12 }}>You don't have permission to view this screen. Contact your admin if you need access.</div>
    </div>
  );
}

function Protected({ children, screenKey, superAdminOnly }) {
  const { user, loading, hasAccess } = useAuth();
  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (superAdminOnly && !user.is_super_admin) return <Layout><AccessDenied /></Layout>;
  if (screenKey && !hasAccess(screenKey)) return <Layout><AccessDenied /></Layout>;
  return <Layout>{children}</Layout>;
}

// Same auth/permission logic as Protected, but for the 3 TV dashboards —
// renders inside TvShell (no sidebar/topbar) instead of the normal Layout.
function TvProtected({ children, screenKey }) {
  const { user, loading, hasAccess } = useAuth();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (screenKey && !hasAccess(screenKey)) return <TvShell><AccessDenied /></TvShell>;
  return <TvShell>{children}</TvShell>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Navigate to="/masters" replace />} />

          {/* MASTERS */}
          <Route path="/masters" element={<Protected screenKey="masters_home"><MastersHome /></Protected>} />
          <Route path="/masters/divisions" element={<Protected screenKey="masters_divisions"><DivisionMaster /></Protected>} />
          <Route path="/masters/factories" element={<Protected screenKey="masters_factories"><FactoryMaster /></Protected>} />
          <Route path="/masters/shift-lines" element={<Protected screenKey="masters_shiftlines"><ShiftLineMaster /></Protected>} />
          <Route path="/masters/operations" element={<Protected screenKey="masters_operations"><OperationMaster /></Protected>} />
          <Route path="/masters/fund-groups" element={<Protected screenKey="masters_fundgroups"><FundGroupMaster /></Protected>} />
          <Route path="/masters/machines" element={<Protected screenKey="masters_machines"><MachineMaster /></Protected>} />
          <Route path="/masters/employees" element={<Protected screenKey="masters_employees"><EmployeeMaster /></Protected>} />
          <Route path="/masters/downtime-reasons" element={<Protected screenKey="masters_downtime_reasons"><DowntimeReasonMaster /></Protected>} />

          {/* PLANNING */}
          <Route path="/ob" element={<Protected screenKey="ob"><OperationBulletin /></Protected>} />
          <Route path="/line-design" element={<Protected screenKey="line_design"><LineDesign /></Protected>} />

          {/* OPERATIONS */}
          <Route path="/deployment" element={<Protected screenKey="deployment"><Deployment /></Protected>} />
          <Route path="/daily-balancing" element={<Protected screenKey="daily_balancing"><DailyBalancing /></Protected>} />
          <Route path="/hourly" element={<Protected screenKey="hourly_reporting"><HourlyReporting /></Protected>} />

          {/* ANDROID PWA — no Layout wrapper, full screen */}
          <Route path="/android" element={<AndroidShell />} />

          {/* REPORTS */}
          <Route path="/skill-matrix" element={<Protected screenKey="skill_matrix"><SkillMatrix /></Protected>} />
          <Route path="/skill-journey" element={<Protected screenKey="skill_journey"><SkillJourney /></Protected>} />

          {/* DASHBOARDS — TV shell, no sidebar/topbar */}
          <Route path="/dashboard/supervisor" element={<TvProtected screenKey="dashboard_supervisor"><SupervisorDashboard /></TvProtected>} />
          <Route path="/dashboard/plant" element={<TvProtected screenKey="dashboard_plant"><PlantHeadDashboard /></TvProtected>} />
          <Route path="/dashboard/downtime" element={<TvProtected screenKey="dashboard_downtime"><DowntimeDashboard /></TvProtected>} />

          {/* PLANT OPTIMISER */}
          <Route path="/plant-optimiser" element={<Protected screenKey="plant_optimiser"><PlantOptimiser /></Protected>} />

          {/* MONTHLY SUMMARY */}
          <Route path="/monthly-summary" element={<Protected screenKey="monthly_summary"><MonthlySummary /></Protected>} />

          {/* ADMIN */}
          <Route path="/admin/users" element={<Protected superAdminOnly><UserAccess /></Protected>} />
          <Route path="/admin/roles" element={<Protected superAdminOnly><RoleMaster /></Protected>} />

          <Route path="*" element={<Navigate to="/masters" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}